import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Sparkles, TrendingDown, RotateCcw, Star, ArrowRight } from "lucide-react";
import { axios, API, logError } from "../lib/api";
import { getImageUrl } from "./ToyCard";

/**
 * Marketplace home hero — surfaces up to 6 listings split into:
 *  - **Paid promotions** (sellers who paid £0.50 to feature)
 *  - **Price drops** (lowered in the last 7 days, largest saving first)
 *  - **Just relisted** (freshly back on the marketplace)
 *
 * Each card carries a reason pill so buyers know why it's highlighted.
 */
export const HighlightsStrip = () => {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${API}/marketplace/highlights`);
        if (!cancelled) setItems(res.data?.items || []);
      } catch (err) {
        logError("HighlightsStrip: failed to load highlights", err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  if (loading) return null;
  if (!items.length) return null;

  return (
    <section
      className="bg-gradient-to-br from-[#FFF6C7] to-[#FFEFB8] border-b-2 border-gray-900 py-10"
      data-testid="highlights-strip"
    >
      <div className="max-w-7xl mx-auto px-6">
        <header className="flex items-center justify-between mb-6 flex-wrap gap-2">
          <h2 className="text-2xl sm:text-3xl font-black flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
            <Sparkles className="w-6 h-6 text-[#FF6B6B]" />
            This week's highlights
          </h2>
          <button
            type="button"
            onClick={() => navigate("/browse")}
            className="text-sm font-bold inline-flex items-center gap-1 hover:underline"
            data-testid="highlights-browse-all"
          >
            Browse all <ArrowRight className="w-4 h-4" />
          </button>
        </header>
        <ul className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {items.map((item) => (
            <HighlightCard key={item.id} item={item} />
          ))}
        </ul>
      </div>
    </section>
  );
};

const REASON_META = {
  promoted: { Icon: Star, label: "PROMOTED", cls: "bg-[#FFD93D] text-gray-900" },
  price_drop: { Icon: TrendingDown, label: "PRICE DROP", cls: "bg-[#10B981] text-white" },
  relist: { Icon: RotateCcw, label: "BACK IN STOCK", cls: "bg-[#4A90E2] text-white" },
};

const HighlightCard = ({ item }) => {
  const navigate = useNavigate();
  const reason = REASON_META[item.highlight_reason] || REASON_META.promoted;
  const { Icon, label, cls } = reason;
  const priceLabel = item.is_donation
    ? "FREE"
    : item.transaction_type === "swap"
    ? "SWAP"
    : `£${Number(item.price || 0).toFixed(2)}`;

  return (
    <li>
      <button
        type="button"
        onClick={() => navigate(`/listing/${item.id}`)}
        className="group w-full bg-white border-2 border-gray-900 rounded-xl overflow-hidden shadow-[3px_3px_0px_0px_rgba(17,24,39,1)] hover:translate-x-[-1px] hover:translate-y-[-1px] hover:shadow-[4px_4px_0px_0px_rgba(17,24,39,1)] transition-all text-left"
        data-testid={`highlight-card-${item.id}`}
      >
        <div className="relative aspect-square overflow-hidden bg-gray-100">
          <img
            src={getImageUrl(item.images)}
            alt={item.title}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
          />
          <span
            className={`absolute top-1.5 left-1.5 inline-flex items-center gap-1 text-[9px] font-black px-1.5 py-0.5 rounded-full border-2 border-gray-900 ${cls}`}
            data-testid={`highlight-reason-${item.id}`}
          >
            <Icon className="w-2.5 h-2.5" />{label}
          </span>
        </div>
        <div className="p-2">
          <p className="font-bold text-xs truncate">{item.title}</p>
          <p className="text-sm font-black text-[#FF6B6B]">{priceLabel}</p>
        </div>
      </button>
    </li>
  );
};

export default HighlightsStrip;
